import { createReducer, on } from '@ngrx/store';
import { IAccessChangeStudent } from 'src/app/core/models/access';
import { IStudent } from 'src/app/core/models/user';
import { AttendanceFilter, IAttendanceRecord } from '../../models/academic';
import { resetAppState } from '../global/global.actions';
import { StudentActions, } from './student.actions';

export const itemsFeatureKey = 'student';

export interface StudentState {
  students: IStudent[];
  studentsForAccesschange: IAccessChangeStudent[];
  selectedStudentId: string | null; // Changed from selectedStudent: IStudent | null
  // Separate attendance state
  attendanceRecords: Record<string, IAttendanceRecord[]>;
  attendanceLoading: Record<string, boolean>;
  attendanceFilters: AttendanceFilter;
  loading: boolean;
  error: any;
}

export const initialState: StudentState = {
  students: [],
  studentsForAccesschange: [],
  selectedStudentId: null, // Changed from selectedStudent: null
  attendanceRecords: {},
  attendanceLoading: {},
  attendanceFilters: {
    Absent: true,
    Late: true,
    LeftEarly: true,
    MissedSubjects: true,
    Term1: true,
    Term2: true,
    Term3: true,
    Term4: true,
    YearToDate: false,
  },
  loading: false,
  error: null,
};

export const studentReducer = createReducer(
  initialState,
  on(StudentActions.loadStudents, (state) => ({
    ...state,
    loading: true,
    error: null
  })),
  on(StudentActions.loadStudentsSuccess, (state, { students }) => ({
    ...state,
    loading: false,
    students
  })),
  on(StudentActions.loadStudentsFailure, (state, { error }) => ({
    ...state,
    loading: false,
    error
  })),
  on(StudentActions.loadStudentsForAccesschange, (state) => ({
    ...state,
    loading: true,
    error: null
  })),
  on(StudentActions.loadStudentsForAccesschangeSuccess, (state, { students }) => ({
    ...state,
    loading: false,
    studentsForAccesschange: students
  })),
  on(StudentActions.loadStudentsForAccesschangeFailure, (state, { error }) => ({
    ...state,
    loading: false,
    error
  })),
  on(StudentActions.addStudent, (state, { student }) => ({
    ...state,
    students: [...state.students, student]
  })),
  // Add handlers for update student picture success and failure
  on(StudentActions.updateStudentSuccess, (state, { student }) => ({
    ...state,
    students: state.students.map(s => (s.id === student.id ? student : s)),
  })),
  on(StudentActions.updateStudentFailure, (state, { error }) => ({
    ...state,
    error
  })),
  on(StudentActions.deleteStudent, (state) => {
    const selectedStudentId = state.selectedStudentId;

    if (!selectedStudentId) {
      return state; // If no student is selected, return the current state unchanged
    }

    return {
      ...state,
      students: state.students.filter(student => student.id !== selectedStudentId),
      selectedStudentId: null // Clear the selected student ID
    };
  }),
  on(StudentActions.selectStudent, (state, { id }) => ({
    ...state,
    selectedStudentId: id // Just store the ID
  })),
  on(StudentActions.setAttendanceFilters, (state, { filters }) => ({
    ...state,
    attendanceFilters: filters
  })),
  on(StudentActions.resetAttendanceFilters, (state) => ({
    ...state,
    attendanceFilters: initialState.attendanceFilters
  })),
  // New attendance reducers
  on(StudentActions.loadStudentAttendance, (state, { studentId }) => ({
    ...state,
    attendanceLoading: {
      ...state.attendanceLoading,
      [studentId]: true
    }
  })),
  on(StudentActions.loadStudentAttendanceSuccess, (state, { studentId, attendance }) => ({
    ...state,
    attendanceRecords: {
      ...state.attendanceRecords,
      [studentId]: attendance
    },
    attendanceLoading: {
      ...state.attendanceLoading,
      [studentId]: false
    }
  })),
  on(StudentActions.loadStudentAttendanceFailure, (state, { studentId, error }) => ({
    ...state,
    attendanceLoading: {
      ...state.attendanceLoading,
      [studentId]: false
    },
    error
  })),
  on(resetAppState, () => initialState)
);
